import React from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import type { NestedAdfRendererWrapper } from '@confluence/macro-utils/entry-points/nestedAdfRenderer.ts';

import { ExcerptType } from '../hooks/useParsedMacroParams.ts';

const styles = cssMap({
	excerpt: {
		color: token('color.text.subtle'),
		transform: 'scale(0.75)',
		transformOrigin: 'top left',
	},
});

interface ContentExcerptProps {
	adfDocument: DocNode;
	mediaToken: string;
	excerptType: ExcerptType;
	contentId: string;
	spaceKey: string;
	spaceId?: string | null;
	RendererWrapper: NestedAdfRendererWrapper;
}

const parseAdfDocument = (adf?: string | null): DocNode | null => {
	if (!adf) {
		return null;
	}
	try {
		return JSON.parse(adf) as DocNode;
	} catch {
		return null;
	}
};

export const hasExcerptContent = (
	excerptType: ExcerptType,
	excerpt?: string | null,
): DocNode | false => {
	if (excerptType === ExcerptType.None) {
		return false;
	}
	const adfDocument = parseAdfDocument(excerpt);
	if (
		adfDocument &&
		adfDocument.content &&
		adfDocument.content.length > 0 &&
		adfDocument.content.some(
			(node: DocNode['content'][number]): boolean =>
				'content' in node && Array.isArray(node.content) && node.content.length > 0,
		)
	) {
		return adfDocument;
	}
	return false;
};

export const ContentExcerpt = function ContentExcerpt({
	adfDocument,
	excerptType,
	mediaToken,
	contentId,
	spaceKey,
	spaceId,
	RendererWrapper,
}: ContentExcerptProps): React.JSX.Element {
	return (
		<Box xcss={styles.excerpt}>
			<RendererWrapper
				adf={adfDocument.content}
				mediaToken={mediaToken}
				targetContentId={contentId}
				targetSpaceKey={excerptType === ExcerptType.RichContent ? spaceKey : ''}
				targetSpaceId={excerptType === ExcerptType.RichContent ? (spaceId ?? undefined) : undefined}
			/>
		</Box>
	);
};
